/** Woods that are a boundary, not a garnish.
 *
 * `SCENERY` in catalog.ts scatters single blocking trees over open ground at a
 * few per hundred cells, which is the right texture for a meadow and reads as
 * litter when what the map wants is a wood. The classic's forests are not scattered
 * trees: they are solid blocks of canopy several cells across that a hero has
 * to walk round, and they are what makes a region feel enclosed when there is
 * no mountain to do it.
 *
 * So this pass grows THICKETS: a handful of compact blobs of blocking woodland
 * per map, each a few rows of horizontal runs. Runs, because the wood art is
 * the same kind of art as the range art - `game/tools/range-kit.py` cuts one
 * painted belt of forest into 64px columns, and consecutive columns reassemble
 * seamlessly, so a run of them is one unbroken canopy with no sky between the
 * crowns. Rows stacked one behind another are drawn back to front by the
 * renderer's ground-line sort, so a two- or three-deep thicket gains real
 * depth instead of reading as a hedge.
 *
 * Safety is `tryPlaceAt`'s, not this module's. Every wood column blocks, and
 * `tryPlaceAt` floods the map after each blocking placement and takes the
 * placement back if it cut any anchor off; a thicket therefore cannot wall a
 * town, a mine or a gate away from the rest of the map however unlucky the
 * seed. This pass runs before the reachability audit for the same reason.
 */

import type { GenState } from "./state.js";
import { allNeighbours, idx, terrainOf, walkable, type Grid } from "./grid.js";
import { placeCore } from "./place.js";
import { int } from "./rng.js";
import type { AdventureMapState, ContentRegistry, TerrainKind } from "@game/core";

type Payload = Readonly<Record<string, number | string | boolean | null>>;

/** A belt of wood: the ordered columns one generated image was cut into. */
interface Belt {
  readonly columns: readonly string[];
}

/**
 * The belts, counted out of the content set rather than written down, for the
 * same reason `obstacleWalls.ts` counts its massifs: how many columns a belt
 * has is a fact about its art, and re-authoring one taller re-cuts it. A stale
 * count would fail silently, because `tryPlaceAt` answers null for a def that
 * does not exist exactly as it does for a cell it does not like.
 */
function belt(content: ContentRegistry, id: string): Belt {
  const columns: string[] = [];
  for (let k = 0; content.mapObjects.has(`obs_wood_${id}${String(k)}`); k++) {
    columns.push(`obs_wood_${id}${String(k)}`);
  }
  return { columns };
}

/** What grows where, by belt id. A terrain absent from this table grows none. */
const WOODS: Partial<Record<TerrainKind, readonly string[]>> = {
  grass: ["oak_a", "mixed_a", "pine_a"],
  dirt: ["oak_a", "mixed_a"],
  rough: ["pine_a", "mixed_a"],
  snow: ["pine_snow_a"],
  highlands: ["pine_a", "mixed_a"],
};

/** The one-tile clumps that ragged a thicket's edge; see `raggedEdge`. */
const CLUMPS: Partial<Record<TerrainKind, string>> = {
  grass: "obs_wood_clump_oak",
  dirt: "obs_wood_clump_oak",
  rough: "obs_wood_clump_pine",
  snow: "obs_wood_clump_pine",
  highlands: "obs_wood_clump_pine",
};

/** One thicket per this many cells of map. */
const CELLS_PER_THICKET = 420;
/** How many seeds to try per thicket wanted; most land, some do not. */
const TRIES_PER_THICKET = 6;

const nothing = (): Payload => ({});

/** Grow the map's thickets. Returns how many wood sprites landed. */
export function growThickets(s: GenState): number {
  const { map: m, g } = s;
  const want = Math.trunc((g.width * g.height) / CELLS_PER_THICKET);
  let grown = 0;
  for (let n = 0; n < want; n++) {
    for (let t = 0; t < TRIES_PER_THICKET; t++) {
      const x = int(s.r, g.width);
      const y = int(s.r, g.height);
      if (!plantable(m, g, x, y)) continue;
      const ids = WOODS[terrainOf(m, idx(g, x, y))];
      if (ids === undefined) continue;
      const belts = ids.map((id) => belt(s.content, id)).filter((b) => b.columns.length > 0);
      if (belts.length === 0) continue;
      const planted = grow(s, belts, x, y);
      if (planted > 0) { grown += planted + raggedEdge(s, x, y); break; }
    }
  }
  return grown;
}

/**
 * One thicket at (x, y): two or three rows of runs, each row offset from the
 * one behind it so the block has a ragged edge rather than a square one.
 */
function grow(s: GenState, belts: readonly Belt[], x: number, y: number): number {
  const belt = belts[int(s.r, belts.length)]!;
  const rows = 2 + int(s.r, 2);
  let planted = 0;
  for (let row = 0; row < rows; row++) {
    const from = x - 1 + int(s.r, 3);
    const len = 2 + int(s.r, belt.columns.length - 1);
    planted += layRun(s, belt, from, y + row, len);
  }
  return planted;
}

/**
 * A few one-tile clumps around a thicket, so its outline is a wood's and not a
 * rectangle's.
 *
 * A belt is cut into full-tile columns and laid in rows, which is what makes it
 * read as unbroken canopy - and also what gives it four straight sides. The
 * clumps are single trees grown together, drawn narrower and lower than a belt
 * column, so a handful of them scattered on the cells just outside the block
 * break the line the way the edge of a real wood is broken.
 */
function raggedEdge(s: GenState, x: number, y: number): number {
  const clump = CLUMPS[terrainOf(s.map, idx(s.g, x, y))];
  if (clump === undefined || !s.content.mapObjects.has(clump)) return 0;
  let planted = 0;
  for (let k = 0; k < 4; k++) {
    const cx = x - 2 + int(s.r, 6);
    const cy = y - 1 + int(s.r, 5);
    if (plantable(s.map, s.g, cx, cy) && place(s, clump, cx, cy)) planted++;
  }
  return planted;
}

/** A run of `len` cells of one wood belt, laid west to east from (x, y). */
function layRun(s: GenState, belt: Belt, x: number, y: number, len: number): number {
  const cols = belt.columns;
  let at = 0;
  let planted = 0;
  while (at < len) {
    const take = Math.min(len - at, cols.length);
    const from = int(s.r, cols.length - take + 1);
    for (let k = 0; k < take; k++) {
      const cx = x + at;
      if (plantable(s.map, s.g, cx, y) && place(s, cols[from + k]!, cx, y)) planted++;
      at++;
    }
  }
  return planted;
}

/** One sprite on one cell. False when the placer refused it. */
function place(s: GenState, defId: string, x: number, y: number): boolean {
  return placeCore(s, defId, x, y, nothing()) >= 0;
}

/**
 * May a tree stand here?
 *
 * `tryPlaceAt` refuses an occupied, reserved or roaded cell already; what it
 * cannot know is that a wood planted flush against a road pinches the road
 * between two blocking cells, which is legal, ugly, and the sort of thing that
 * turns a two-cell corridor into a one-cell one. So the road's whole apron is
 * kept clear here - all eight neighbours, which is the rule the rest of the
 * generator already keeps and `mapgen-landform.spec.ts` already asserts. Four
 * was not enough: a thicket set diagonally against a road still crowds it.
 */
function plantable(m: AdventureMapState, g: Grid, x: number, y: number): boolean {
  if (x < 0 || y < 0 || x >= g.width || y >= g.height) return false;
  const i = idx(g, x, y);
  if (!walkable(m, i) || m.occupant[i] !== 0 || m.road[i] !== 0) return false;
  if (WOODS[terrainOf(m, i)] === undefined) return false;
  const buf: number[] = [];
  for (const nb of allNeighbours(g, i, buf)) if (m.road[nb] !== 0) return false;
  return true;
}
